import { AfterViewInit, Component, OnDestroy, signal, ViewChild } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { firstValueFrom, Subscription } from "rxjs";
import LocalizationConstants from "src/app/constants/localization-constants";
import { TechCoverContact } from "src/app/models/TechCoverContact";
import { TechCoverContactEdit } from "src/app/models/TechCoverContactEdit";
import { AuthService } from "src/app/services/auth.service";
import { CompanyService } from "src/app/services/company.service";
import { ModalConfig, ModalService } from "src/app/services/modal.service";
import { TechCoverContactService } from "src/app/services/tech-cover-contact.service";

import { ConfirmationDialogData } from "../../common/confirmation-dialog/confirmation-dialog-modal.component";
import { ContactsCreateModalComponent } from "../contacts-create-modal/contacts-create-modal.component";

@Component({
  selector: "app-contacts",
  templateUrl: "./contacts.component.html",
  styleUrl: "./contacts.component.scss",
  standalone: false,
})
export class ContactsComponent implements AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  readonly localizationConstants = LocalizationConstants;
  readonly faSpinner = faSpinner;
  private subscriptions: Subscription[] = [];

  isLoading = signal(true);
  dataSource = signal(new MatTableDataSource<TechCoverContactEdit>([]));

  constructor(
    private techCoverContactService: TechCoverContactService,
    private companyService: CompanyService,
    public modalService: ModalService,
    private authService: AuthService,
  ) {
    this.loadContactsAsync();
  }

  ngAfterViewInit() {
    this.dataSource.update((dataSource) => {
      dataSource.sort = this.sort;
      dataSource.paginator = this.paginator;
      return dataSource;
    });
  }

  async openCreateModal() {
    const famCodes = await this.loadFamCodesAsync();
    const modalConfig = new ModalConfig();
    modalConfig.width = "400px";
    modalConfig.maxHeight = "600px";
    modalConfig.data = {
      famCodes: famCodes,
    };

    const result = await firstValueFrom(
      this.modalService.openModal(ContactsCreateModalComponent, modalConfig),
    );
    if (!result) return;
    this.addTechCoverContact(result);
  }

  private async loadContactsAsync() {
    this.isLoading.set(true);
    try {
      const contacts = await this.techCoverContactService.getAllContactsForEdit();
      this.dataSource.update((dataSource) => {
        dataSource.data = contacts;
        return dataSource;
      });
    } finally {
      this.isLoading.set(false);
    }
  }

  private async loadFamCodesAsync(): Promise<string[]> {
    const famCodes = this.authService.user.isCountryAdmin
      ? this.authService.user.famCodes
      : await this.companyService.getAllFamCodes();

    return famCodes.filter((x) => !this.dataSource().data.some((y) => x === y.famCode));
  }

  async addTechCoverContact(newContact: TechCoverContact) {
    this.isLoading.set(true);
    try {
      await this.techCoverContactService.add(newContact);
      await this.loadContactsAsync();
    } finally {
      this.isLoading.set(false);
      this.modalService.close();
    }
  }

  async updateContact(contact: TechCoverContactEdit) {
    contact.isItemLoading = true;
    contact.email = contact.modifiedEmail ?? contact.email;
    contact.phone = contact.modifiedPhone ?? contact.phone;
    try {
      await this.techCoverContactService.update(contact);
      contact.isEditing = false;
    } finally {
      contact.isItemLoading = false;
    }
  }

  onEmailInput(event: Event, contact: TechCoverContactEdit) {
    contact.modifiedEmail = (event.target as HTMLInputElement).value;
  }

  onPhoneInput(event: Event, contact: TechCoverContactEdit) {
    contact.modifiedPhone = (event.target as HTMLInputElement).value;
  }

  public onDeleteContact(contact: TechCoverContactEdit): void {
    const resultObservable = this.modalService.openConfirmationModal(
      new ConfirmationDialogData(
        LocalizationConstants.TECH_COVER_CONTACT_REMOVE_CONTACT_DIALOG_TITLE,
        [LocalizationConstants.TECH_COVER_CONTACT_REMOVE_CONTACT_DIALOG_CONTENT],
      ),
    );

    const subscription = resultObservable.subscribe((result) => {
      if (result) {
        this.deleteContact(contact);
      }
    });

    this.subscriptions.push(subscription);
  }

  private async deleteContact(contact: TechCoverContactEdit) {
    contact.isItemLoading = true;
    try {
      await this.techCoverContactService.delete(contact);
      this.loadContactsAsync();
    } finally {
      contact.isItemLoading = false;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x.unsubscribe());
  }
}
